/**
 * ClampedTextCell — caps a free-text cell at two lines and, only when the
 * text actually overflows that clamp, hands off to `PopOutList` so hovering
 * reveals the full text. A cell that fits within two lines renders the bare
 * clamped span with no popout wiring, so hovering it does nothing.
 */

import { type ReactNode, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { HIDE_NATIVE_SCROLLBAR, ScrollbarOverlay } from '@/components/ui/scrollbar-overlay';
import { POPOUT_CHROME_WIDTH, PopOutList } from './PopOutList';
import { useClampOverflow } from './useClampOverflow';

interface ClampedTextCellProps {
  text: string | null | undefined;
  className?: string;
  /** CSS length capping the hovered popout, which then scrolls. Uncapped when omitted. */
  maxExpandedHeight?: string;
  /** CSS height of the clamped preview's bottom fade. Full height when omitted. */
  previewFadeHeight?: string;
}

export function ClampedTextCell({
  text,
  className,
  maxExpandedHeight,
  previewFadeHeight,
}: ClampedTextCellProps) {
  const { containerRef, previewRef, isTruncated, cellWidth, collapsedHeight } =
    useClampOverflow<HTMLSpanElement>([text]);

  if (text == null || text === '') {
    return <span className={className}>—</span>;
  }

  const clampedSpan = (
    <span ref={previewRef} className={cn(className, 'line-clamp-2')}>
      {text}
    </span>
  );

  return (
    <div ref={containerRef}>
      {isTruncated ? (
        <PopOutList
          width={cellWidth + POPOUT_CHROME_WIDTH}
          collapsedHeight={collapsedHeight}
          previewFadeHeight={previewFadeHeight}
          collapsed={clampedSpan}
          expanded={
            maxExpandedHeight ? (
              <ScrollingContent className={className} maxHeight={maxExpandedHeight}>
                {text}
              </ScrollingContent>
            ) : (
              // `line-clamp-none` rather than a bare span: the clamped preview is a
              // -webkit-box whose border box starts half a line above its first glyph,
              // while a plain inline span's box *is* its glyphs. PopOutList aligns the
              // two boxes, so without matching the display the text would land 2px higher.
              <span className={cn(className, 'line-clamp-none')}>{text}</span>
            )
          }
        />
      ) : (
        clampedSpan
      )}
    </div>
  );
}

/**
 * A plain block, not the preview's `-webkit-box`: that box starts half a line
 * above its first glyph, which a scroll container renders as a sliced first line.
 * Uses the design-system scrollbar and fades the bottom edge while there is more
 * to scroll to. The negative margin pulls the lane into the card's padding so it
 * sits 6px from the border; the right padding clears the 12px lane plus a 6px gap.
 */
export function ScrollingContent({
  children,
  className,
  maxHeight,
}: {
  children: ReactNode;
  className?: string;
  maxHeight: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [moreBelow, setMoreBelow] = useState(false);

  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (el) setMoreBelow(el.scrollHeight - el.scrollTop - el.clientHeight > 1);
  }, []);

  useLayoutEffect(measure, [measure, children]);

  return (
    <div className="relative -mr-1.5">
      <div
        ref={scrollRef}
        onScroll={measure}
        style={{ maxHeight }}
        className={cn(className, 'block overflow-y-auto pr-[18px]', HIDE_NATIVE_SCROLLBAR)}
      >
        <div>{children}</div>
      </div>
      <ScrollbarOverlay targetRef={scrollRef} />
      {moreBelow && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute bottom-0 left-0 right-3 h-8 bg-gradient-to-t from-off-white to-transparent"
        />
      )}
    </div>
  );
}
